import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing4x, spacing10x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { zIndex10000 } from '../../styles/zIndex.js';
import { roleMap } from '../Alert/Alert.js';
import { Toast } from '../Toast/Toast.js';
import { useToasts, useToastsStateContext } from '../ToastsContext/ToastsContext.js';
import { Transition } from '../Transition/Transition.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var _excluded = ["destroyedToastIds", "onDestroy"];
var ToastsWrapper = styled.div.withConfig({
  displayName: "ToastsContainer__ToastsWrapper",
  componentId: "sc-h0emp5-0"
})(["&&{position:fixed;z-index:", ";pointer-events:all;bottom:0;inset-inline-start:", ";max-height:100%;overflow-y:auto;padding-block-end:", ";padding-inline-end:", ";-ms-overflow-style:none;scrollbar-width:none;}&& ::-webkit-scrollbar{display:none;}"], zIndex10000 + 1, spacing4x, spacing4x, spacing10x);
var transitionDefinition = {
  entered: {
    style: {
      insetInlineStart: "0px",
      opacity: 1,
      position: "relative"
    },
    transition: {
      duration: 800,
      easing: "ease-in-out",
      property: "inset-inline-start, opacity"
    }
  },
  entering: {
    style: {
      insetInlineStart: "-400px",
      opacity: 0,
      position: "relative"
    },
    transition: {
      duration: 800,
      easing: "ease-in-out",
      property: "inset-inline-start, opacity"
    }
  },
  exiting: {
    style: {
      opacity: 0
    },
    transition: {
      duration: 500,
      easing: "ease-in-out",
      property: "opacity"
    }
  }
};
var ToastsContainer = /*#__PURE__*/React.forwardRef(function ToastsContainer2(props, ref) {
  var _props$destroyedToast = props.destroyedToastIds,
    destroyedToastIds = _props$destroyedToast === void 0 ? [] : _props$destroyedToast,
    _onDestroy = props.onDestroy,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var _useToasts = useToasts(),
    removeToast = _useToasts.removeToast;
  var _useToastsStateContex = useToastsStateContext(),
    toasts = _useToastsStateContex.toasts;
  function renderToasts(filteredToasts, ariaProps) {
    return /*#__PURE__*/jsx(TransitionGroup, _objectSpread(_objectSpread({}, ariaProps), {}, {
      children: filteredToasts.map(function (toast) {
        return /*#__PURE__*/jsx(Transition, {
          appear: true,
          in: true,
          transition: transitionDefinition,
          children: function children(_ref) {
            var transitionRef = _ref.ref,
              style = _ref.style;
            return /*#__PURE__*/jsx(Toast, _objectSpread({
              ref: transitionRef,
              style: style,
              onClose: function onClose() {
                return removeToast(toast.id);
              },
              onDestroy: function onDestroy() {
                return _onDestroy && _onDestroy(toast.id);
              }
            }, toast));
          }
        }, toast.id);
      })
    }));
  }
  var toastsByRole = toasts.filter(function (toast) {
    return !destroyedToastIds.includes(toast.id);
  }).reduce(function (output, toast) {
    var CTAs = toast.CTAs,
      destroyable = toast.destroyable,
      _toast$variant = toast.variant,
      variant = _toast$variant === void 0 ? "info" : _toast$variant;
    if (CTAs && CTAs.length > 0 || destroyable) {
      output.alertdialog.push(toast);
    } else {
      output[roleMap[variant]].push(toast);
    }
    return output;
  }, {
    status: [],
    alert: [],
    alertdialog: []
  });
  return /*#__PURE__*/jsxs(ToastsWrapper, _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref,
    children: [renderToasts(toastsByRole.status, {
      role: "region",
      "aria-live": "polite",
      "aria-label": t("ToastsContainer.status")
    }), renderToasts(toastsByRole.alert, {
      role: "region",
      "aria-live": "assertive",
      "aria-label": t("ToastsContainer.alert")
    }), renderToasts(toastsByRole.alertdialog)]
  }));
});
ToastsContainer.displayName = "ToastsContainer";

export { ToastsContainer };
